/**
 * Sending a file to secure storage, part by part, from the browser.
 *
 * The bytes never pass through the console's own process: it hands the browser
 * one URL per part and the browser writes straight into the holding area
 * (`docs/API.md`, Versions). Those URLs are credentials with a clock on them,
 * so nothing in this module logs one, puts one in an error message, or keeps
 * one after the part it belongs to has been accepted.
 *
 * Only the console seals the file afterwards: this returns the identifiers the
 * storage gave back for each part, and `POST …/complete` is what turns them
 * into an object.
 */

import type { CompletedPart, UploadPart } from "../api/types";

/** What a caller is told while the file is on its way. */
export interface UploadProgress {
  /** Bytes the storage has accepted so far. */
  bytes_sent: number;
  parts_done: number;
  parts_total: number;
}

export interface UploadOptions {
  /** Called after each accepted part. */
  onProgress?: (progress: UploadProgress) => void;
  /**
   * Ask the console for fresh URLs.
   *
   * Part URLs go stale after `upload_url_ttl_seconds`, and a large file over a
   * hotel connection outlives them. Re-opening the same version is idempotent
   * while it is waiting for its bytes and returns new URLs for the parts not
   * yet accepted, which is how an upload carries on instead of starting again.
   */
  resign?: () => Promise<UploadPart[]>;
  signal?: AbortSignal;
  /** Injectable for tests; production uses the browser's own `fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * Raised when a part could not be written.
 *
 * Its message is written for the person watching the wizard and never carries
 * the URL that failed: that URL is a credential, and an error string is the
 * one place a credential most reliably ends up being copied.
 */
export class UploadFailed extends Error {
  readonly partNumber: number;
  readonly status: number | null;

  constructor(message: string, partNumber: number, status: number | null) {
    super(message);
    this.name = "UploadFailed";
    this.partNumber = partNumber;
    this.status = status;
  }
}

/** Answers that mean "this URL has aged out", rather than "this failed". */
function isExpired(status: number): boolean {
  return status === 400 || status === 401 || status === 403;
}

/**
 * Write one part and return what the storage called it.
 *
 * Args:
 *     file: The file being sent.
 *     part: The part to write, with its byte range and its URL.
 *     doFetch: The `fetch` to use.
 *     signal: Abort signal.
 *
 * Returns:
 *     The storage's identifier for the part.
 *
 * Raises:
 *     UploadFailed: When the storage refused the part, or accepted it without
 *         returning an identifier the console can seal the file with.
 */
async function putPart(
  file: File,
  part: UploadPart,
  doFetch: typeof fetch,
  signal?: AbortSignal,
): Promise<CompletedPart> {
  const blob = file.slice(part.first_byte, part.last_byte + 1);
  let response: Response;
  try {
    response = await doFetch(part.url, {
      method: "PUT",
      body: blob,
      signal,
    });
  } catch (error) {
    // A cancellation is not a failure, and must keep behaving as one.
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    // The browser got no answer at all: a preflight the bucket refused, a name
    // that does not resolve, a connection that dropped. Left alone this
    // surfaces as the browser's own "Failed to fetch" — which is not a
    // sentence this console would ever write.
    throw new UploadFailed(
      `The browser could not reach secure storage for part ${part.part_number}. ` +
        "Check the connection and try again; if it never works from here, the " +
        "platform team has to let this address write to the holding area.",
      part.part_number,
      null,
    );
  }
  if (!response.ok) {
    throw new UploadFailed(
      `Part ${part.part_number} of the file was not accepted (${response.status}).`,
      part.part_number,
      response.status,
    );
  }
  const etag = response.headers.get("ETag");
  if (!etag) {
    throw new UploadFailed(
      `Secure storage accepted part ${part.part_number} without saying what it ` +
        "called it, so the console cannot finish the file. Try again.",
      part.part_number,
      null,
    );
  }
  return { part_number: part.part_number, etag };
}

/**
 * Send every part of a file, in order, and report what arrived.
 *
 * One part at a time on purpose: a person uploading a 20 GB file on office
 * wifi is sharing that connection with everybody else in the room, and four
 * parallel streams buy minutes at the cost of the meeting nobody could hear.
 *
 * Args:
 *     file: The file the person picked.
 *     parts: The plan the console signed.
 *     options: Progress callback, the re-signing hook, an abort signal and an
 *         injectable `fetch`.
 *
 * Returns:
 *     One entry per part, in part order, ready for `POST …/complete`.
 *
 * Raises:
 *     UploadFailed: When a part could not be written, even after fresh URLs.
 */
export async function uploadParts(
  file: File,
  parts: UploadPart[],
  options: UploadOptions = {},
): Promise<CompletedPart[]> {
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const done: CompletedPart[] = [];
  let plan = [...parts];
  let bytesSent = 0;
  let resigned = false;

  for (let index = 0; index < plan.length; index += 1) {
    const part = plan[index]!;
    let written: CompletedPart;
    try {
      written = await putPart(file, part, doFetch, options.signal);
    } catch (error) {
      const expired =
        error instanceof UploadFailed &&
        error.status !== null &&
        isExpired(error.status);
      if (!expired || resigned || !options.resign) {
        throw error;
      }
      // One re-signing per upload: a second stale answer is a refusal about
      // the request rather than about the clock, and retrying for ever would
      // hide it behind a progress bar that never moves.
      resigned = true;
      const fresh = await options.resign();
      const remaining = fresh.filter(
        (candidate) => candidate.part_number >= part.part_number,
      );
      plan = [...plan.slice(0, index), ...remaining];
      index -= 1;
      continue;
    }
    done.push(written);
    bytesSent += part.last_byte - part.first_byte + 1;
    options.onProgress?.({
      bytes_sent: bytesSent,
      parts_done: done.length,
      parts_total: plan.length,
    });
  }

  return done.sort((left, right) => left.part_number - right.part_number);
}
